import { useState, useRef, useEffect } from "react";
import { analyzeDocument } from "../utils/api";

const getUploadUrl = async (sourcePath) => {
  const siteConf = (window.site && window.site.config) || {};
  const assetSourceBucket = siteConf.assetSourceBucket || "";
  const base = (window.site && window.site.submissionApiBaseUrl) || "";
  const appname = window.app && window.app.appName;
  const env = window.app && window.app.appEnvName;
  const token = (window.app && window.app.storage && window.app.storage.getItem("token")) || localStorage.getItem("token") || "0381db972d7447216018b15aa1b54dc3e51cd3e282b78b3424fa0726828e766c995495fcc2d8c71d86dc7777ef43ca92ff51f8e7027ba56b9aa0b6a47f76d16ab35076d6c1ffd8fa85f3b1cbf4a04a05ccebb5cb80a59f8687d5c0fb4cf5b34cea18c7bea57931c3b34cc3209df2b956e544cf2b2b0cd5601a065ec74ee911db6467905bb959c330fcc01beb2c6a826d25e0e2736dec31a1c25ccd82e65eefa3f5a34a9810151e7be83bd9f533a16e9d8a9124936329e9db585d4aa32b8f052e8fe649f7e11e35afc8d248679af62612ac3ed7ba4b0b114a8b19c645909935ca32260ad829cc54f1b701575cd7c0e7074bb0";
  const resp = await fetch(
    `${base}/uploadURL?appname=${appname}&env=${env}&token=${token}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source_bucket: assetSourceBucket, sourcepath: sourcePath }),
    }
  );
  const data = await resp.json();
  const uploadUrl = data && data.result;
  if (!uploadUrl) throw new Error("Signed URL missing");
  return uploadUrl;
};

export default function StepScanAndVerify({ session, updateSession, onNext, onBack }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [docS3Key, setDocS3Key] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [error, setError] = useState("");
  const [countries, setCountries] = useState([]);
  const [regions, setRegions] = useState([]);
  const [form, setForm] = useState({
    firstName: "", lastName: "", middleName: "", dateOfBirth: "",
    address: "", city: "", state: "", zipCode: "", country: "",
    documentNumber: "",
  });
  const fileRef = useRef();

  // Fetch countries on mount
  useEffect(() => {
    const commonApi = (window.site && window.site.config && window.site.config.commonApi) || "";
    const appname = (window.app && window.app.appName) || "envoidemo";
    if (!commonApi) return;
    fetch(`${commonApi}/getcountries?&appname=${appname}`, { method: "POST" })
      .then(r => r.json())
      .then(data => { if (data?.result) setCountries(data.result); })
      .catch(err => console.error("getcountries error", err));
  }, []);

  const fetchRegions = (alpha3) => {
    const commonApi = (window.site && window.site.config && window.site.config.commonApi) || "";
    if (!commonApi || !alpha3) { setRegions([]); return; }
    fetch(`${commonApi}/getregions?&alpha3=${alpha3}`, { method: "POST" })
      .then(r => r.json())
      .then(data => { if (data?.result) setRegions(data.result); else setRegions([]); })
      .catch(err => console.error("getregions error", err));
  };

  const handleCountryChange = (alpha3) => {
    setForm(f => ({ ...f, country: alpha3, state: "" }));
    fetchRegions(alpha3);
  };

  // After scan, match extracted country name → alpha3 and load its regions
  useEffect(() => {
    if (!extracted || !countries.length || !form.country) return;
    const alreadyAlpha3 = countries.find(c => c.alpha3 === form.country);
    if (alreadyAlpha3) { fetchRegions(alreadyAlpha3.alpha3); return; }
    const match = countries.find(c =>
      c.countryname?.toLowerCase() === form.country?.toLowerCase()
    );
    if (match) {
      setForm(f => ({ ...f, country: match.alpha3 }));
      fetchRegions(match.alpha3);
    }
  }, [extracted, countries]); // eslint-disable-line

  const handleFile = (f) => {
    if (!f) return;
    setFile(f);
    setExtracted(null);
    setDocS3Key(null);
    setError("");
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target.result);
    reader.readAsDataURL(f);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f) handleFile(f);
  };

  const handleScan = async () => {
    if (!file) return setError("Please upload a photo of your document first.");
    setScanning(true); setError(""); setUploadProgress(0);
    try {
      const appname = window.app && window.app.appName;
      const siteConf = (window.site && window.site.config) || {};
      const cdnBase = siteConf.sourceCloudFront || siteConf.proxiesCloudFront || "";
      const cleanFileName = file.name.replace(/\s+/g, "");
      const ext = cleanFileName.split(".").pop();
      const base = cleanFileName.replace(`.${ext}`, "");
      const sourcePath = `${appname}/faceverify/${session.userId || "guest"}/${base}${Date.now()}.${ext}`;

      // 1. Get pre-signed S3 upload URL
      setUploadProgress(10);
      const signedUrl = await getUploadUrl(sourcePath);
      setUploadProgress(20);

      // 2. PUT file to S3 via XHR (with progress)
      const s3Key = await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.upload.addEventListener("progress", (e) => {
          if (e.lengthComputable) {
            setUploadProgress(Math.round(20 + (e.loaded / e.total) * 60));
          }
        });
        xhr.addEventListener("load", () =>
          xhr.status >= 200 && xhr.status < 300 ? resolve(sourcePath) : reject(new Error("S3 upload failed"))
        );
        xhr.addEventListener("error", () => reject(new Error("S3 upload failed")));
        xhr.open("PUT", signedUrl);
        xhr.setRequestHeader("Content-Type", file.type);
        xhr.send(file);
      });
      setDocS3Key(s3Key);
      setUploadProgress(80);

      // 3. Analyze document using the S3 key
      const resultAPI = await analyzeDocument(s3Key, session.docType);
      setUploadProgress(100);
      let fields = resultAPI?.result?.extracted || {};
      setExtracted(fields);
      setForm({
        firstName: fields?.firstName || "",
        lastName: fields?.lastName || "",
        middleName: fields?.middleName || "",
        dateOfBirth: fields?.dateOfBirth || "",
        address: fields?.address || "",
        city: fields?.city || "",
        state: fields?.state || "",
        zipCode: fields?.zipCode || "",
        country: fields?.country || "",
        documentNumber: fields?.documentNumber || "",
        issueDate: fields?.issueDate || "",
        idType: fields?.idType || "",
        expirationDate: fields?.expirationDate || "",
        placeOfBirth: fields?.placeOfBirth || "",
        mrzCode: fields?.mrzCode || "",
      });
    } catch (e) { setError(e.message); }
    finally { setScanning(false); setUploadProgress(0); }
  };

  const handleSubmit = () => {
    const required = ["firstName", "lastName", "address", "city", "state", "zipCode", "country"];
    for (const f of required) {
      if (!form[f]) return setError(`Please fill in: ${f.replace(/([A-Z])/g, " $1").toLowerCase()}`);
    }
    // File already uploaded to S3 during scan — just pass the key forward
    updateSession({ idS3Key: docS3Key, extracted: form, profile: form });
    onNext();
  };

  const field = (label, key, type = "text", opts = {}) => (
    <div className="field-group" key={key}>
      <label>{label}</label>
      {opts.select ? (
        <select value={form[key]} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}>
          {opts.options.map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
      ) : (
        <input
          type={type}
          value={form[key]}
          onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
          placeholder={opts.placeholder || ""}
        />
      )}
    </div>
  );

  const docLabel = session.docType === "PASSPORT" ? "passport" : "driver's license / ID";

  return (
    <div className={`step-card${extracted ? " wide" : ""}`}>
      <div className="step-icon"><span className="material-symbols-outlined">photo_camera</span></div>
      <h1>Scan your {docLabel}</h1>
      <p className="step-sub">Upload a clear photo, then confirm the details we extract from it.</p>

      {error && <div className="error-banner">{error}</div>}

      <div className="scan-row">
        <div className="scan-col">
          <div
            className={`upload-zone ${file ? "has-file" : ""}`}
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileRef.current?.click()}
          >
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              style={{ display: "none" }}
              onChange={(e) => handleFile(e.target.files[0])}
            />
            {preview ? (
              <div className="upload-preview">
                <img src={preview} alt="ID preview" />
                <button
                  className="replace-btn"
                  onClick={(e) => { e.stopPropagation(); setFile(null); setPreview(null); setExtracted(null); }}
                >
                  Replace
                </button>
              </div>
            ) : (
              <div className="upload-prompt">
                <span className="upload-icon">⬆</span>
                <span>Drag &amp; drop or click to upload</span>
                <span className="upload-hint">JPEG, PNG, PDF · Max 10 MB</span>
              </div>
            )}
          </div>

          {file && !extracted && (
            <>
              <button className="btn-primary" onClick={handleScan} disabled={scanning}>
                {scanning ? <><span className="spinner" /> {uploadProgress < 80 ? `Uploading… ${uploadProgress}%` : "Analyzing document…"}</> : "Scan document →"}
              </button>
              {scanning && uploadProgress > 0 && (
                <div style={{ marginTop: "10px", height: "4px", background: "rgba(255,255,255,0.1)", borderRadius: "2px", overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${uploadProgress}%`, background: "var(--highlight, #e9c548)", borderRadius: "2px", transition: "width 0.3s ease" }} />
                </div>
              )}
            </>
          )}

          {!extracted && (
            <button className="btn-ghost" style={{ marginTop: "1rem" }} onClick={onBack}>← Back</button>
          )}
        </div>

        {extracted && (
          <div className="scan-col extracted-section">
            <div className="extracted-header">
              <span className="check-icon">✓</span>
              <span>Information extracted — please verify or correct below</span>
            </div>

            <div className="form-grid two-col">
              {field("First name", "firstName", "text", { placeholder: "Jane" })}
              {field("Last name", "lastName", "text", { placeholder: "Smith" })}
              {field("Middle name (optional)", "middleName", "text", { placeholder: "Marie" })}
              {field("Date of birth", "dateOfBirth", "text", { placeholder: "MM/DD/YYYY" })}
              {field("Place of birth", "placeOfBirth", "text", { placeholder: "City, State" })}
              {field("Document number", "documentNumber", "text", { placeholder: "DL or passport number" })}
              {field("Issue date", "issueDate", "text", { placeholder: "MM/DD/YYYY" })}
              {field("Expiration date", "expirationDate", "text", { placeholder: "MM/DD/YYYY" })}
              {field("ID", "idType", "text", { placeholder: "ID type" })}
              {field("MRZ code", "mrzCode", "text", { placeholder: "Machine Readable Zone code" })}
            </div>

            <hr className="form-divider" />
            <p className="form-section-label">Address on document</p>

            <div className="form-grid one-col">
              {field("Street address", "address", "text", { placeholder: "123 Main St" })}
            </div>
            <div className="form-grid three-col">
              {field("City", "city", "text", { placeholder: "Springfield" })}
              <div className="field-group">
                <label>State / Region</label>
                <select
                  value={form.state}
                  onChange={(e) => setForm(f => ({ ...f, state: e.target.value }))}
                  disabled={!regions.length}
                >
                  <option value="">Select state / region</option>
                  {regions.map((r, i) => (
                    <option key={i} value={r.regionname}>{r.regionname}</option>
                  ))}
                </select>
              </div>
              {field("ZIP code", "zipCode", "text", { placeholder: "12345" })}
            </div>
            <div className="form-grid one-col">
              <div className="field-group">
                <label>Country</label>
                <select
                  value={form.country}
                  onChange={(e) => handleCountryChange(e.target.value)}
                >
                  <option value="">Select country</option>
                  {countries.map((c, i) => (
                    <option key={i} value={c.alpha3}>{c.countryname}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="action-row">
              <button className="btn-ghost" onClick={onBack}>← Back</button>
              <button className="btn-primary" onClick={handleSubmit}>
                Confirm &amp; continue →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
